import { captureException } from '@sentry/react'
import classNames from 'classnames'
import { useState } from 'react'

import type { ReactElement, RefObject } from 'react'
import type { DropzoneRef, FileWithPath } from 'react-dropzone'
import type { IFileUploaded } from '~/types/file'

import Dropzone from '~/components/Dropzone'
import MwMLoader from '~/components/MwMLoader'
import { useCreateFileMutation, useUploadFileMutation } from '~/queries/files'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  accept?: Record<string, string[]>
  className?: string
  dropzoneRef?: RefObject<DropzoneRef>
  multiple?: boolean
  noVisibleZone?: boolean
  onDrop: (files: IFileUploaded[]) => void
  onLoadingChange?: (isLoading: boolean) => void
}

const UploadFileInput = ({
  accept,
  className,
  dropzoneRef,
  multiple = true,
  noVisibleZone = false,
  onDrop,
  onLoadingChange,
}: Props): ReactElement => {
  /** Local state */

  const [isUploading, setIsUploading] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: createFile } = useCreateFileMutation()

  const { mutateAsync: uploadFile } = useUploadFileMutation()

  /** Handlers */

  const handleDrop = async (files: FileWithPath[]) => {
    const uploadedFiles: IFileUploaded[] = new Array(files.length)
    setIsUploading(true)
    onLoadingChange && onLoadingChange(true)

    try {
      await Promise.all(
        files.map(async (file, index) => {
          const tempFile = await createFile({
            mimeType: file.type,
            name: file.name,
            size: file.size,
          })
          await uploadFile({ file, mimeType: file.type, uploadUrl: tempFile.upload_url })
          uploadedFiles[index] = tempFile
        })
      )
      onDrop(uploadedFiles)
    } catch (e) {
      captureException(e)
      displayToast('Something went wrong', 'error')
    } finally {
      setIsUploading(false)
      onLoadingChange && onLoadingChange(false)
    }
  }

  return (
    <div className={classNames('upload-file-input-component', className, { 'is-hidden': noVisibleZone })}>
      <Dropzone
        accept={accept}
        dropzoneRef={dropzoneRef}
        isLoading={isUploading}
        multiple={multiple}
        onDrop={handleDrop}
      />
      {isUploading && noVisibleZone ? <MwMLoader category="item" /> : null}
    </div>
  )
}

export default UploadFileInput
